<template>
  <div>
    <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 sm:mb-6">
      <h1 class="text-2xl sm:text-3xl font-bold">หมวดหมู่สินค้า</h1>
      <router-link to="/admin/categories/create" class="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark font-bold text-center">
        + เพิ่มหมวดหมู่
      </router-link>
    </div>

    <!-- Mobile Card View -->
    <div class="block sm:hidden space-y-4">
      <div v-if="loading" class="p-8 text-center text-gray-500">
        กำลังโหลด...
      </div>
      <template v-else>
        <div v-for="category in categories" :key="category.id" class="bg-white rounded-lg shadow p-4">
          <div class="flex justify-between items-start mb-3">
            <div>
              <p class="font-bold text-gray-900">{{ category.name }}</p>
              <p class="text-sm text-gray-500">{{ category.slug }}</p>
            </div>
            <span 
              class="px-2 py-1 text-xs font-semibold rounded-full"
              :class="category.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'"
            >
              {{ category.is_active ? 'ใช้งาน' : 'ปิดใช้งาน' }}
            </span>
          </div>
          <div class="flex gap-2">
            <router-link :to="`/admin/categories/${category.id}/edit`" class="flex-1 text-center text-primary hover:text-primary-dark text-sm font-medium py-2 border border-primary rounded">
              แก้ไข
            </router-link>
            <button @click="deleteCategory(category.id)" class="flex-1 text-center text-red-600 hover:text-red-900 text-sm font-medium py-2 border border-red-300 rounded">
              ลบ
            </button>
          </div>
        </div>
        <div v-if="categories.length === 0" class="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          ไม่มีข้อมูลหมวดหมู่
        </div>
      </template>
    </div>

    <!-- Desktop Table View -->
    <div class="hidden sm:block bg-white rounded-lg shadow overflow-hidden">
      <div v-if="loading" class="p-8 text-center text-gray-500">
        กำลังโหลด...
      </div>
      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ชื่อหมวดหมู่</th>
              <th class="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Slug</th>
              <th class="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">สถานะ</th>
              <th class="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">จัดการ</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="category in categories" :key="category.id">
              <td class="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ category.name }}</td>
              <td class="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ category.slug }}</td>
              <td class="px-4 lg:px-6 py-4 whitespace-nowrap">
                <span 
                  class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                  :class="category.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'"
                >
                  {{ category.is_active ? 'ใช้งาน' : 'ปิดใช้งาน' }}
                </span>
              </td>
              <td class="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                <router-link :to="`/admin/categories/${category.id}/edit`" class="text-primary hover:text-primary-dark">แก้ไข</router-link>
                <button @click="deleteCategory(category.id)" class="text-red-600 hover:text-red-900">ลบ</button>
              </td>
            </tr>
            <tr v-if="categories.length === 0">
                <td colspan="4" class="px-6 py-4 text-center text-gray-500">ไม่มีข้อมูลหมวดหมู่</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <Pagination 
        :current-page="pagination.current_page" 
        :last-page="pagination.last_page" 
        @page-change="fetchCategories" 
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import api from '../../api';
import { useNotification } from '../../composables/useNotification';
import Pagination from '../../components/Pagination.vue';

const categories = ref([]);
const pagination = ref({ current_page: 1, last_page: 1 });
const loading = ref(true);
const { showSuccess, showError, confirmDelete, handleApiError } = useNotification();

const fetchCategories = async (page = 1) => {
    loading.value = true;
    try {
        const response = await api.get(`/admin/categories?page=${page}`);
        categories.value = response.data.data.data;
        pagination.value = {
            current_page: response.data.data.current_page,
            last_page: response.data.data.last_page
        };
    } catch (error) {
        showError('ไม่สามารถโหลดข้อมูลหมวดหมู่ได้');
    } finally {
        loading.value = false;
    }
};

const deleteCategory = async (id) => {
    const confirmed = await confirmDelete('หมวดหมู่นี้');
    if (confirmed) {
        try {
            // Check if category has products
            const checkResponse = await api.get(`/admin/categories/${id}/check-products`);
            
            if (checkResponse.data.has_products) {
                showError('ไม่สามารถลบหมวดหมู่นี้ได้เนื่องจากมีสินค้าอยู่ กรุณาย้ายสินค้าไปหมวดหมู่อื่นก่อน');
                return;
            }
            
            // Check if category has children
            if (checkResponse.data.has_children) {
                showError('ไม่สามารถลบหมวดหมู่นี้ได้เนื่องจากมีหมวดหมู่ย่อยอยู่ กรุณาลบหมวดหมู่ย่อยก่อน');
                return;
            }
            
            await api.delete(`/admin/categories/${id}`);
            showSuccess('ลบหมวดหมู่เรียบร้อยแล้ว');
            fetchCategories(pagination.value.current_page);
        } catch (error) {
            handleApiError(error);
        }
    }
};

onMounted(() => {
    fetchCategories();
});
</script>